import {getPostalCodeRegexp} from '@smart/utils';
import React from 'react';
import {useCallback, useMemo} from 'react';
import type {StringSchema} from 'yup';
import {array, date, number, string} from 'yup';
import {AppModelContext} from '../components/app';
import type {allSchemas} from '../components/easy-form/types';

/*

[1] address
[2] city
[2] country
[2] contact [email, sms, personal call, other]
[6] dateOfBirth
[5] email
[1] firstName
[2] language
[1] lastName
[3] message
[4] phoneNumber
[1] postCode
[1] salutation
[1] subject

1 - SingleLine
2 - OneOf(list)
3 - MultiOf(list)
3 - LargeText
4 - Phone
5 - email
6 - dateUntil

*/

export function useSchemas() {
  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
    {};

  const msg: any = useMemo(
    () => ({
      required: cmsTranslations.validations_required,
      stringMin: cmsTranslations.validations_string_min,
      stringMax: cmsTranslations.validations_string_max,
      arrayMin: cmsTranslations.validations_array_min,
      arrayMax: cmsTranslations.validations_array_max,
      notAnOption: cmsTranslations.validations_notAnOption,
      outOfFormat: cmsTranslations.validations_outOfFormat,
      dateInvalid: cmsTranslations.validations_dateInvalid,
    }),
    [cmsTranslations],
  );

  const requireSchema = useCallback(
    (schema: allSchemas, required: boolean = false) => {
      return required ? schema.required(msg.required) : schema;
    },
    [msg],
  );

  const minSchema = useCallback(
    (schema: StringSchema, required: boolean, min: number) => {
      return schema.test(`minlength`, msg.stringMin, (value: any) => {
        const v: string = value || ``;

        if (required) {
          return v.length >= (min || 1);
        }

        return v.length > 0 ? v.length >= min : true;
      });
    },
    [msg],
  );

  return useMemo(
    () => ({
      singleLine: (required?: boolean, min: number = 0, max: number = 128) => {
        // const schema = string().trim().max(128, msg.stringMax).test();
        return minSchema(
          requireSchema(string().trim().max(max, msg.stringMax), required),
          !!required,
          min,
        );
      },

      oneOf: (list: Array<string>, required?: boolean) => {
        const schema = string().oneOf(list, msg.notAnOption);
        return requireSchema(schema, required);
      },

      multiOf: (list: Array<string>, minRequired: number = 0) => {
        const schema = array(string().oneOf(list, msg.notAnOption))
          .min(minRequired, msg.arrayMin)
          .max(list.length, msg.arrayMax);
        return schema;
      },

      largeText: (required?: boolean) => {
        const schema = string().max(1024, msg.stringMax);
        return requireSchema(schema, required);
      },

      subject: (required?: boolean) => {
        const subjectRegExp =
          /^[\a-zA-Z0-9p{L}']{2,100}([' -]{1}[\a-zA-Z0-9p{L}]{1,20}){0,5}$/;
        const schema = string().matches(subjectRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      contactLargeText: (required?: boolean) => {
        const largeTextRegExp = /^[^<>]{2,32000}$/;
        const schema = string().matches(largeTextRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      contactSubject: (required?: boolean) => {
        const subjectRegExp = /^[^<>]{2,255}$/;
        const schema = string().matches(subjectRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      number: (required?: boolean) => {
        const schema = number();
        return requireSchema(schema, required);
      },

      email: (required?: boolean) => {
        const emailRegExp =
          /^([A-Za-z0-9_!#$%&'*+\/=?`{|}~^.-]+)@([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(\.[a-zA-Z]{2,})?$/;
        const schema = string().matches(emailRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      phone: (required?: boolean) => {
        const phoneRegExp = /^[1-9]{0,3}([ -]{0,1}[1-9]{1}[0-9]{5,11})$/;
        const schema = string().matches(phoneRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      phoneCode: (required?: boolean) => {
        const phoneRegExp = /^[+][0-9]{1,3}$/;
        const schema = string().matches(phoneRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      dateUntil: (untilDate: Date, required?: boolean) => {
        const schema = date()
          .nullable()
          .transform((curr, orig) => (orig === `` ? null : curr))
          .max(untilDate, msg.dateInvalid);

        return !required ? schema : schema.required(msg.required);
      },

      city: (required?: boolean) => {
        const cityCodeRegExp =
          /^[^¡?÷¿\\ˆ~!@#$%^&*()_+\=\[\]{};':"\|\/,.<>?¢0123456789]{0,40}$/;
        const schema = string().matches(cityCodeRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      street: (required?: boolean) => {
        const streetCodeRegExp = /^[^¡?÷¿ˆ~!@#$%^&*()_+\=\[\]{};"<>?¢]{0,256}$/;
        const schema = string().matches(streetCodeRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      postalCode: (countryCode: string | undefined, required?: boolean) => {
        const postalCodeRegExp = getPostalCodeRegexp(countryCode ?? ``);
        const schema = string().matches(postalCodeRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      companyName: (required?: boolean) => {
        const companyNameCodeRegExp =
          /^[^-\s¡?÷¿\\ˆ~!@#$%^&*()_+\=\[\]{};:"\|\/,.<>?¢0123456789]{1,40}([' -]{1}[^-\s¡?÷¿\\ˆ~!@#$%^&*()_+\=\[\]{};:"\|\/,.<>?¢]{1,40}){0,5}$/;
        const schema = string().matches(companyNameCodeRegExp, {
          message: msg.outOfFormat,
          excludeEmptyString: true,
        });
        return requireSchema(schema, required);
      },

      position: (required?: boolean) => {
        const schema = string().max(100, msg.stringMax);
        return requireSchema(schema, required);
      },

      firstAndLastNames: (required?: boolean) => {
        const regEx =
          /^[^-\s¡?÷¿\\ˆ~!@#$%^&*()_+\=\[\]{};:"\|\/,.<>?¢0123456789]{2,20}([' -]{1}[^-\s¡?÷¿\\ˆ~!@#$%^&*()_+\=\[\]{};:"\|\/,.<>?¢0123456789]{1,20}){0,2}$/;
        const schema = string().test(`regExMatch`, msg.outOfFormat, (value) => {
          const trimmedValue = value?.trim();
          return Boolean(trimmedValue?.match(regEx));
        });
        return requireSchema(schema, required);
      },
    }),
    [cmsTranslations, requireSchema, minSchema, msg],
  );
}
